import { api, Core, useApiPlay } from "@ancademy/vse-client";
import { IGame, ISubGame, TAddManySubGameInput } from "@ancademy/vse-share";
import { BasePlay, Desc, GameTypeConfigList, Md } from "@client";
import { Color, Style } from "@client/style";
import { css } from "@emotion/css";
import { ISubCreateParams, SubNamespace } from "@share";
import { subViewList } from "@sub";
import { Button, Card, Divider, Modal, Popover, Space, Tabs } from "antd";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ICreateParams, IGameState, IMoveParams, IPlayerState, IPushParams, MoveType, PushType } from "../../config";
import { Header } from "./Header";
import { Welcome } from "./Welcome";

const { TabPane } = Tabs;

function geneSubGameParams(namespace: string) {
  let groupSize = 1;
  let roundsParams: Partial<ISubCreateParams>[] = [];
  switch (namespace as SubNamespace) {
    case SubNamespace.Concept: {
      roundsParams = [{}];
      break;
    }
    case SubNamespace.RentHouse: {
      roundsParams = [{ searchCost: 0 }, { searchCost: 50 }, { searchCost: 200 }, { searchCost: 200 }];
      break;
    }
    case SubNamespace.FindJob: {
      roundsParams = [
        { searchCost: 0, recall: true },
        { searchCost: 0, recall: false },
        { searchCost: 100, recall: false },
        { searchCost: 100, recall: false },
      ];
      break;
    }
    case SubNamespace.BuyTicket: {
      roundsParams = [{ searchCost: 0 }, { searchCost: 0 }, { searchCost: 20 }, { searchCost: 20 }];
      break;
    }
    case SubNamespace.BuyCar: {
      roundsParams = [{ searchCost: 0 }, { searchCost: 0 }, { searchCost: 500 }, { searchCost: 1000 }];
      break;
    }
    case SubNamespace.BuyInsurance: {
      roundsParams = [
        { searchCost: 0 },
        { searchCost: 0, buyInsurance_secondLimit: 30 },
        { searchCost: 100, buyInsurance_secondLimit: 30 },
        { searchCost: 100, buyInsurance_secondLimit: 15 },
      ];
      break;
    }
    case SubNamespace.BuyHouse: {
      roundsParams = [{ searchCost: 0 }, { searchCost: 0 }, { searchCost: 10000 }, { searchCost: 10000 }];
      break;
    }
    case SubNamespace.SaleFurniture: {
      roundsParams = Array(4)
        .fill(null)
        .map(() => ({}));
      break;
    }
    case SubNamespace.SaleCar: {
      roundsParams = Array(4)
        .fill(null)
        .map(() => ({}));
      break;
    }
    case SubNamespace.TradeCar: {
      roundsParams = Array(4)
        .fill(null)
        .map(() => ({}));
      break;
    }
    case SubNamespace.Bargain: {
      groupSize = 6;
      roundsParams = Array(3)
        .fill(null)
        .map(() => ({ searchCost: 0 }));
      break;
    }
  }
  const firstRound = roundsParams[0],
    lastRound = roundsParams[roundsParams.length - 1];
  firstRound.practice = true;
  firstRound.showIntro = true;
  lastRound.showTest = true;
  roundsParams.forEach((round, r) => {
    round.r = r;
    if (r === 0) {
      round.practice = true;
      round.showIntro = true;
    }
    if (r === roundsParams.length - 1) {
      round.showTest = true;
    }
  });
  return roundsParams as ISubCreateParams[];
}

async function initState(game: IGame<ICreateParams>): Promise<IPlayerState> {
  const result = await api.getMyResult(game.id);
  const subGameParams: TAddManySubGameInput<ISubCreateParams> = [];
  for (let i = 0; i < subViewList.length; i++) {
    const { namespace, label } = subViewList[i];
    const roundParamList = geneSubGameParams(namespace);
    const behaviorScore = ~~(game.params.behaviorScore / subViewList.length),
      questionScore = ~~(game.params.questionScore / subViewList.length);
    subGameParams.push(
      ...roundParamList.map((params, j) => ({
        namespace,
        params: {
          ...params,
          stepInfo:
            j === roundParamList.length - 1
              ? {
                  resultId: result.id,
                  stepAmount: subViewList.length + 1,
                  behaviorScore,
                  questionScore,
                  stepResult: {
                    seq: i + 1,
                    title: label,
                    expectTime: 180,
                    maxScore: behaviorScore + questionScore,
                  },
                }
              : null,
        },
      }))
    );
  }
  const games: ISubGame<ISubCreateParams>[] = (await api.addManySubGame(game.id, subGameParams)) as any;
  return {
    inited: true,
    welcomed: false,
    subGameList: games.map(({ id, namespace, params: { r } }) => ({
      id,
      namespace: namespace as SubNamespace,
      r,
      over: false,
    })),
  };
}

export function Play({
  game,
}: Core.IPlayProps<ICreateParams, IGameState, IPlayerState, MoveType, PushType, IMoveParams, IPushParams>) {
  const navigate = useNavigate();
  const { apiState, setApiState } = useApiPlay<IPlayerState>();
  useEffect(() => {
    if (apiState.inited) {
      return;
    }
    initState(game).then(setApiState);
  }, []);
  if (!apiState.inited) {
    return null;
  }
  const { welcomed, subGameList } = apiState;
  if (!welcomed) {
    return (
      <BasePlay>
        <Welcome done={() => setApiState((s) => (s.welcomed = true))} />
      </BasePlay>
    );
  }
  return (
    <BasePlay>
      <Header gameId={game.id} subGameList={subGameList} />
      <Card
        className={css`
          position: fixed;
          max-width: ${Md.pageWidth};
          max-height: 80vh;
          margin: auto;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
        `}
      >
        <Tabs
          size="large"
          tabPosition="left"
          defaultActiveKey={GameTypeConfigList.find(({ subNamespaceList }) =>
            subNamespaceList.some((subNamespace) =>
              subGameList.find(({ namespace, over }) => namespace === subNamespace && !over)
            )
          )?.type.toString()}
          onChange={(key) => console.log(key)}
        >
          {GameTypeConfigList.map(({ type, label, videoUrl, desc, subNamespaceList }) => (
            <TabPane
              tab={
                <div
                  className={css`
                    color: ${Color.main};
                  `}
                >
                  {label}
                </div>
              }
              key={type}
              className={css`
                padding: 2rem 5rem 2rem 2rem;
                display: flex;
                flex-direction: column;
                min-height: 28rem;
                animation: antFadeIn 1s;
              `}
            >
              <div
                className={css`
                  max-height: 30rem;
                  overflow-y: auto;
                `}
              >
                <Desc desc={desc} withTitle={true} />
              </div>
              <Divider />
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    display: videoUrl ? "flex" : "none",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    Modal.info({
                      ...Md.fnProps,
                      content: (
                        <video
                          width="100%"
                          style={{
                            display: "block",
                            margin: "0 auto 1rem",
                          }}
                          src={videoUrl}
                          controls
                          autoPlay
                        />
                      ),
                    })
                  }
                >
                  <video src={videoUrl} muted autoPlay style={{ width: "15rem", margin: "auto" }} />
                  <span
                    className={css`
                      opacity: 0.5;
                    `}
                  >
                    点击查看视频简介
                  </span>
                </div>
                <Divider type="vertical" />
                <div style={{ margin: "auto" }}>
                  <Popover
                    placement="bottom"
                    trigger="click"
                    content={
                      <Space>
                        {subNamespaceList.map((namespace) => {
                          const { label } = subViewList.find((s) => namespace === s.namespace);
                          const subGame = subGameList.find((s) => s.namespace === namespace && !s.over);
                          if (subGame) {
                            return (
                              <Button
                                key={namespace}
                                className={Style.btnPrimarySm}
                                onClick={() => navigate(`/${subGame.namespace}/${subGame.id}`)}
                              >
                                {label}
                              </Button>
                            );
                          } else {
                            return (
                              <Button key={namespace} disabled={true} className={Style.btnDisabledSm}>
                                {label}
                                (已完成)
                              </Button>
                            );
                          }
                        })}
                      </Space>
                    }
                  >
                    <Button className={Style.btnPrimary}>开始实验</Button>
                  </Popover>
                </div>
              </div>
            </TabPane>
          ))}
        </Tabs>
      </Card>
    </BasePlay>
  );
}
